import { logDebug, logError } from '@ptek/client-logger';
import { useWidgetContext, useWidgetEvents } from '@ptek/widget-core';
import { useCallback, useContext } from 'react';
import { WIDGET_EVENTS } from '../../../constants/events';
import { AppContext } from '../../../context/AppContext';
import { useRecordingStore } from '../../../stores';
import { AmbientScribeContext, AutoExportConfig } from '../../../types';
import { DocumentState } from '../../../types/document';
import { useAutoExportConfig } from '../../../utils/feature-util';
import { TranscriptProcessor } from '../../../utils/transcriptUtils';

interface UseAutoExportDocumentReturn {
  /** Whether auto-export is enabled */
  isAutoExportEnabled: boolean;
  /** The auto-export configuration (null if disabled) */
  autoExportConfig: AutoExportConfig | null;
  /** Perform auto-export of a document */
  performAutoExport: (
    documentState: DocumentState,
    options?: {
      onSuccess?: () => void;
      onError?: (error: Error) => void;
    }
  ) => boolean;
}

/**
 * Auto-export document after generation. Fire-and-forget — emits
 * EXPORT_DOCUMENT synchronously; host-side failures are not observable.
 */
export function useAutoExportDocument(): UseAutoExportDocumentReturn {
  const { emitToHost } = useWidgetEvents();
  const autoExportConfig = useAutoExportConfig();
  const { widgetContext } = useWidgetContext<AmbientScribeContext>();
  const { activeRecordingSession, savedTranscriptData } = useRecordingStore();
  const { sessionModel } = useContext(AppContext);

  // Use the same export action as the manual export flow
  const configurableMenuActions =
    widgetContext.context?.configurableMenuActions;
  const primaryExportAction =
    configurableMenuActions?.primaryExportAction?.type || '';

  const performAutoExport = useCallback(
    (
      documentState: DocumentState,
      options?: {
        onSuccess?: () => void;
        onError?: (error: Error) => void;
      }
    ): boolean => {
      if (!autoExportConfig?.autoExportAfterGeneration || !documentState) {
        logDebug(
          'useAutoExportDocument',
          'Auto-export skipped: not enabled or no document',
          {
            autoExportAfterGeneration:
              autoExportConfig?.autoExportAfterGeneration,
            hasDocument: !!documentState,
          }
        );
        return false;
      }

      const exportAction = primaryExportAction;

      let exportData;

      try {
        const encounterId =
          activeRecordingSession?.encounterId ||
          activeRecordingSession?.id ||
          'unknown';
        const sessionId = activeRecordingSession?.id || encounterId;
        const patientName =
          activeRecordingSession?.patientName || 'Unknown Patient';

        const transcriptText =
          TranscriptProcessor.toPlainTextWithOverrides(
            sessionModel.transcript,
            sessionModel.editOverrides
          ) || savedTranscriptData?.transcriptText;

        exportData = {
          document: {
            id: documentState.id,
            content: documentState.content,
            encounterId,
            sessionId,
            patientName,
            type: 'chart_note' as const,
            documentType: 'SOAP_NOTE',
          },
          exportAction,
          transcriptText,
          widgetState: {
            currentContent: documentState.content,
            isModified: false,
            timestamp: Date.now(),
            isAutoExport: true,
          },
        };
      } catch (error) {
        logError(
          'useAutoExportDocument',
          'Failed to build export payload:',
          error
        );

        options?.onError?.(
          error instanceof Error ? error : new Error(String(error))
        );
        return false;
      }

      emitToHost(WIDGET_EVENTS.EMITTER_NAMES.EXPORT_DOCUMENT, exportData);

      logDebug('useAutoExportDocument', 'Auto-export emitted successfully', {
        documentId: documentState.id,
      });

      if (autoExportConfig.autoCloseAfterExport) {
        // Brief delay to allow UI state to update before closing
        setTimeout(() => {
          emitToHost(WIDGET_EVENTS.EMITTER_NAMES.CONFIRM_CLOSE);
        }, 300);
      }

      options?.onSuccess?.();
      return true;
    },
    [
      autoExportConfig,
      emitToHost,
      activeRecordingSession,
      sessionModel.transcript,
      sessionModel.editOverrides,
      savedTranscriptData,
      primaryExportAction,
    ]
  );

  return {
    isAutoExportEnabled: !!autoExportConfig?.autoExportAfterGeneration,
    autoExportConfig,
    performAutoExport,
  };
}
